'use client';

import { useBodyScrollLock } from '@common/hooks';
import { cn } from '@core/utils';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePublishElementHeight } from '@/hooks/usePublishElementHeight';
import { useResponsiveDialogStore } from '@/stores/DrawerStore/useResponsiveDialogStore';

interface BottomSheetProps {
  /** 상단 다크 토스트 영역 (optional) */
  toast?: ReactNode;
  /** 하단 흰색 영역 내 콘텐츠 */
  children: ReactNode;
  /** 뒤쪽 딤 배경 표시 (외부 상태로 제어) */
  backdrop?: boolean;
  /** backdrop 클릭 시 콜백 */
  onBackdropClick?: () => void;
  className?: string;
  /** 콘텐츠 영역 커스텀 className */
  contentClassName?: string;
}

export const BottomSheet = ({
  toast,
  children,
  backdrop = false,
  onBackdropClick,
  className,
  contentClassName,
}: BottomSheetProps) => {
  const isMobileTablet = useMediaQuery(BREAKPOINTS.MOBILE_TABLET, false);

  if (!isMobileTablet) return null;

  return (
    <BottomSheetContent
      toast={toast}
      backdrop={backdrop}
      onBackdropClick={onBackdropClick}
      className={className}
      contentClassName={contentClassName}
    >
      {children}
    </BottomSheetContent>
  );
};

const BottomSheetContent = ({
  toast,
  children,
  backdrop = false,
  onBackdropClick,
  className,
  contentClassName,
}: BottomSheetProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isToastVisible, setIsToastVisible] = useState(false);
  const isDialogOpen = useResponsiveDialogStore((s) => s.isOpen);
  const sheetRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(backdrop);

  usePublishElementHeight(sheetRef, '--bottom-nav-portal-height');

  useEffect(() => {
    const t1 = setTimeout(() => setIsVisible(true), 100);
    const t2 = setTimeout(() => setIsToastVisible(true), 400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <>
      {/* 백드롭 */}
      {backdrop && (
        <div
          role="presentation"
          onClick={onBackdropClick}
          className="fixed inset-0 z-30 bg-Gray-black/50 transition-opacity duration-300"
        />
      )}

      <div
        ref={sheetRef}
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ease-out',
          isVisible && !isDialogOpen ? 'translate-y-0' : 'translate-y-full',
          className,
        )}
      >
        <div
          className={cn(
            'overflow-hidden shadow-[0_0.25rem_0.25rem_0_rgba(0,0,0,0.06),0_1.25rem_1.25rem_0_rgba(0,0,0,0.04),0.375rem_-0.375rem_1.25rem_0_rgba(0,0,0,0.04)] transition-colors duration-200',
            toast && isToastVisible ? 'bg-Gray-black' : 'bg-Gray-white',
          )}
        >
          {toast && (
            <div
              className={cn(
                'flex items-center justify-center overflow-hidden transition-all duration-200',
                isToastVisible
                  ? 'max-h-40 px-16 py-8 opacity-100'
                  : 'max-h-0 opacity-0',
              )}
            >
              {toast}
            </div>
          )}

          <div
            className={cn(
              'bg-Gray-white px-20 pb-[calc(0.75rem+var(--safe-area-bottom,0px))] pt-12',
              contentClassName,
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
};
